import { Component, input, output, computed, ChangeDetectionStrategy } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { TranslatePipe } from '@core/i18n';
import { ADMIN_ICONS } from '@shared/icons/admin-icons';
import { NAV_SECTIONS } from './admin-sidebar-navigation';

@Component({
  selector: 'app-admin-sidebar',
  standalone: true,
  imports: [RouterLink, RouterLinkActive, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { class: 'flex flex-col flex-1 min-h-0' },
  template: `
    <!-- Logo area -->
    <div
      class="flex items-center border-b border-neutral-800 shrink-0 h-14"
      [class.px-5]="!collapsed()"
      [class.justify-center]="collapsed()"
      [class.px-0]="collapsed()"
    >
      <div class="flex items-center gap-3 min-w-0">
        <!-- Logo mark -->
        <div class="flex items-center justify-center w-9 h-9 bg-primary-500 shrink-0">
          <span class="text-white font-heading text-lg leading-none font-bold">B</span>
        </div>
        @if (!collapsed()) {
          <span class="text-white font-heading text-base font-bold tracking-wide truncate">
            Blisterr Admin
          </span>
        }
      </div>
    </div>

    <!-- Navigation -->
    <nav class="flex-1 overflow-y-auto py-3 no-scrollbar">
      @for (section of navSections; track section.title) {
        <div class="mb-3">
          <!-- Section header -->
          @if (!collapsed()) {
            <p class="px-5 mb-1 text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">
              {{ section.title | translate }}
            </p>
          } @else {
            <div class="mx-auto my-2 w-8 border-t border-neutral-800"></div>
          }

          <!-- Nav items -->
          @for (item of section.items; track item.path) {
            <a
              [routerLink]="item.path"
              routerLinkActive="sidebar-active"
              #rla="routerLinkActive"
              [attr.data-testid]="'nav-' + item.path.slice(1)"
              [attr.aria-current]="rla.isActive ? 'page' : null"
              class="group flex items-center py-2 text-sm transition-colors border-l-[3px] border-transparent hover:bg-neutral-800 hover:text-white"
              [class.px-4]="!collapsed()"
              [class.justify-center]="collapsed()"
              [class.px-0]="collapsed()"
              [attr.title]="collapsed() ? (item.label | translate) : null"
              (click)="navItemClick.emit()"
            >
              <svg
                [class]="'w-5 h-5 shrink-0' + (!collapsed() ? ' mr-3' : '')"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
              >
                <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="item.icon" />
              </svg>
              @if (!collapsed()) {
                <span class="truncate">{{ item.label | translate }}</span>
              }
            </a>
          }
        </div>
      }
    </nav>

    <!-- User area -->
    <div class="border-t border-neutral-800 shrink-0">
      <div
        class="flex items-center py-3"
        [class.px-4]="!collapsed()"
        [class.justify-center]="collapsed()"
        [class.px-0]="collapsed()"
      >
        <!-- Avatar -->
        <div
          class="flex items-center justify-center w-8 h-8 rounded-full bg-primary-500 text-white text-xs font-bold shrink-0"
          [attr.title]="displayUsername()"
        >
          {{ userInitials() }}
        </div>
        @if (!collapsed()) {
          <div class="flex-1 min-w-0 ml-3">
            <p data-testid="sidebar-username" class="text-sm text-white font-medium truncate">
              {{ displayUsername() }}
            </p>
          </div>
          <button
            data-testid="sidebar-logout"
            class="ml-2 p-1 text-neutral-400 hover:text-white transition-colors shrink-0"
            [attr.title]="'auth.logout' | translate"
            [attr.aria-label]="'auth.logout' | translate"
            (click)="logoutClick.emit()"
          >
            <svg
              class="w-5 h-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
            >
              <path stroke-linecap="round" stroke-linejoin="round" [attr.d]="icons.logout" />
            </svg>
          </button>
        }
      </div>

      <!-- Collapse toggle (desktop only) -->
      <button
        class="hidden lg:flex items-center justify-center w-full py-2 border-t border-neutral-800 text-neutral-500 hover:text-white hover:bg-neutral-800 transition-colors"
        [attr.aria-label]="collapsed() ? 'Expand sidebar' : 'Collapse sidebar'"
        (click)="toggleCollapse.emit()"
      >
        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            [attr.d]="collapsed() ? icons.chevronRight : icons.chevronLeft"
          />
        </svg>
      </button>
    </div>
  `,
})
export class AdminSidebarComponent {
  readonly collapsed = input(false);
  readonly username = input<string | null>('');

  readonly navItemClick = output<void>();
  readonly logoutClick = output<void>();
  readonly toggleCollapse = output<void>();

  readonly icons = ADMIN_ICONS;
  readonly navSections = NAV_SECTIONS;

  readonly userInitials = computed(() => {
    const name = this.username();
    if (!name) return '?';
    return name.substring(0, 2).toUpperCase();
  });

  readonly displayUsername = computed(() => this.username() ?? '');
}
